import api from "./axiosConfig";

console.log(' SQUARE API FILE LOADED! ');

// Register passenger with the backend - Step 1 per backend instructions
export const registerPassenger = async (passengerData) => {
  console.log('[SquareAPI] Registering passenger:', JSON.stringify(passengerData, null, 2));
  
  const start = Date.now();
  
  try {
    // Parse name into first and last name
    const nameParts = passengerData.name ? passengerData.name.split(' ') : [];
    const firstName = passengerData.firstName || nameParts[0] || '';
    const lastName = passengerData.lastName || nameParts.slice(1).join(' ') || '';
    
    // DEBUG: Log parsed name parts
    console.log('[SquareAPI] Name parsing:');
    console.log('- Original name:', passengerData.name);
    console.log('- Name parts:', nameParts);
    console.log('- First name:', firstName);
    console.log('- Last name:', lastName);
    console.log('- Email:', passengerData.email);
    
    const apiData = {
      customer_title: 'Mr',
      customer_first_name: firstName,
      customer_last_name: lastName,
      customer_email: passengerData.email,
      customer_cell_no: passengerData.phone
    };
    
    console.log('[API] Step 1: Registering passenger with backend format:', JSON.stringify(apiData, null, 2));
    const response = await api.post("/quote_api/register_passenger", apiData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    const end = Date.now();
    console.log(`[API] /quote_api/register_passenger response time: ${end - start} ms`);
    console.log('[API] Register passenger response:', JSON.stringify(response.data, null, 2));
    
    // Return passenger_id as per backend instructions
    if (response.data && response.data.passenger_id) {
      return { passenger_id: response.data.passenger_id, status: 1, msg: response.data.msg || 'Success' };
    }
    return response.data;
  } catch (error) {
    const end = Date.now();
    console.log(`[API] /quote_api/register_passenger response time (error): ${end - start} ms`);
    console.error("Error registering passenger:", error);
    throw error;
  }
};

// Create booking with the backend - Step 2 per backend instructions
export const createBooking = async (bookingData) => {
  console.log('🔥🔥🔥 SQUARE API CREATE BOOKING FUNCTION CALLED! 🔥🔥🔥');
  console.log('🔥 Raw bookingData:', JSON.stringify(bookingData, null, 2));
  
  const start = Date.now();
  
  // Parse the journey date properly to ensure correct format
  console.log('[SquareAPI] Raw bookingData.journey_date:', bookingData.journey_date);
  const journeyParts = bookingData.journey_date?.split(' ') || [];
  const pickupDate = journeyParts[0] || '09/05/2025';
  const pickupTime = journeyParts.slice(1).join(' ') || '1:00am';
  
  // Create consistent datetime format
  const consistentPickupDatetime = `${pickupDate} ${pickupTime}`;
  
  console.log('[SquareAPI] Parsed datetime values:');
  console.log('- pickupDate:', pickupDate);
  console.log('- pickupTime:', pickupTime);
  console.log('- consistentPickupDatetime:', consistentPickupDatetime);
  
  // Transform data to match exact backend API format per instructions
  const apiData = {
    // Required parameters per backend documentation
    passenger_id: bookingData.passenger_id,
    start: bookingData.start || bookingData.pickup?.address,
    end: bookingData.end || bookingData.dropoff?.address,
    journey_date: consistentPickupDatetime, // Use parsed datetime instead of raw
    fleet_id: bookingData.fleet_id,
    payment_method: bookingData.payment_method || "Square Payment",
    domain: bookingData.domain || window.location.hostname,
    fare_details: {
      selected_fleet_id: bookingData.fleet_id,
      journey_fare: bookingData.fare_details?.journey_fare || "0.00",
      add_charges: bookingData.fare_details?.add_charges || [],
      other_add_charges: bookingData.fare_details?.other_add_charges || [],
      discount: bookingData.fare_details?.discount || "0.00",
      final_fare: bookingData.fare_details?.final_fare || bookingData.amount || "0.00"
    }
  };

  // Only add optional parameters if they have values
  if (bookingData.passenger_count) apiData.passenger_count = bookingData.passenger_count;
  if (bookingData.luggage_count) apiData.luggage_count = bookingData.luggage_count;
  if (bookingData.baby_seat_count) apiData.baby_seat_count = bookingData.baby_seat_count;
  if (bookingData.wheelchair_count) apiData.wheelchair_count = bookingData.wheelchair_count;
  if (bookingData.special_requirements) apiData.special_requirements = bookingData.special_requirements;
  if (bookingData.flight_number) apiData.flight_number = bookingData.flight_number;
  if (bookingData.flight_landing_time) apiData.flight_landing_time = bookingData.flight_landing_time;
  if (bookingData.meet_greet) apiData.meet_greet = bookingData.meet_greet;
  if (bookingData.via_locations) apiData.via_locations = bookingData.via_locations;
  
  try {
    console.log('[API] Step 2: Creating booking with backend format:', JSON.stringify(apiData, null, 2));
    const response = await api.post("/api/quote/confirm", apiData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    const end = Date.now();
    console.log(`[API] /api/quote/confirm response time: ${end - start} ms`);
    console.log('[API] Create booking response:', response.data);
    
    // Return booking_ref_id as per backend instructions
    if (response.data && response.data.status === 1) {
      return {
        status: 1,
        msg: response.data.msg || 'Booking created successfully',
        booking_ref_id: response.data.booking_ref_id || response.data.data?.booking_ref_id,
        passenger_id: response.data.passenger_id || response.data.data?.passenger_id,
        booking_id: response.data.booking_id || response.data.data?.booking_id
      };
    }
    return response.data;
  } catch (error) {
    const end = Date.now();
    console.log(`[API] /quote_api/create_booking response time (error): ${end - start} ms`);
    console.error("Error creating booking:", error);
    console.log('[API] Request data that failed:', JSON.stringify(apiData, null, 2));
    if (error.response) {
      console.log('[API] Error response status:', error.response.status);
      console.log('[API] Error response data:', error.response.data);
      console.log('[API] Error response headers:', error.response.headers);
    }
    throw error;
  }
};

// Process Square payment - Step 3 per backend instructions
export const processSquarePayment = async (paymentData) => {
  const start = Date.now();
  
  // Validate required parameters per backend instructions
  if (!paymentData.booking_ref_id || !paymentData.amount) {
    throw new Error('Missing required fields: booking_ref_id and amount are required');
  }
  
  console.log('[API] Step 3: Processing payment for booking reference:', paymentData.booking_ref_id);
  console.log('[API] Payment amount:', paymentData.amount);
  
  // Format payment data exactly per backend API requirements
  const formattedPaymentData = {
    booking_ref_id: paymentData.booking_ref_id,
    amount: parseFloat(paymentData.amount).toFixed(2), // Ensure 2 decimal places
    ...(paymentData.card_token && {
      card_token: paymentData.card_token,
      card_holder_name: paymentData.card_holder_name || '',
      is_save_cc: paymentData.is_save_cc || '0'
    }),
    ...(paymentData.creditcard_id && {
      creditcard_id: paymentData.creditcard_id
    })
  };
  
  console.log('[API] Step 3: Processing Square payment with backend format:', JSON.stringify(formattedPaymentData, null, 2));
  console.log('[API] Square token received:', paymentData.card_token ? 'Valid' : 'Missing');
  
  // Add 2-second delay to ensure booking is fully processed (per backend team recommendation)
  await new Promise(resolve => setTimeout(resolve, 2000));
  
  // Try primary endpoint first per backend instructions
  try {
    console.log('[API] Attempting primary endpoint: /api/payment/process with booking ref:', formattedPaymentData.booking_ref_id);
    const response = await api.post("/api/payment/process", formattedPaymentData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    const end = Date.now();
    console.log(`[API] /api/payment/process response time: ${end - start} ms`);
    console.log('[API] Payment successful via primary endpoint:', response.data);
    
    if (response.data && (response.data.status === 1 || response.data.success)) {
      return response.data;
    } else {
      throw new Error(response.data?.msg || 'Payment failed');
    }
    
  } catch (error) {
    const errorMsg = error.response?.data?.msg || error.message;
    console.log('[API] Public endpoint failed:', error.response?.status, errorMsg);
    
    // Handle specific Square payment errors
    if (error.response?.status === 400 && errorMsg.includes('GENERIC_DECLINE')) {
      const detailedError = `Payment declined: ${errorMsg}. This could be due to insufficient funds, card security settings, or the bank declining the transaction. Please try a different card or contact your bank.`;
      throw new Error(detailedError);
    }
    
    if (error.response?.status === 400 && errorMsg.includes('CARD_DECLINED')) {
      throw new Error('Your card was declined. Please try a different card or contact your bank.');
    }
    
    if (error.response?.status === 400 && errorMsg.includes('INSUFFICIENT_FUNDS')) {
      throw new Error('Insufficient funds on your card. Please try a different card.');
    }
    
    // If endpoint doesn't exist (404) or booking not found, try alternative endpoints
    if (error.response?.status === 404) {
      console.log('[API] Trying alternative endpoint: /api/booking/make_payment');
      
      try {
        const altResponse = await api.post("/api/booking/make_payment", formattedPaymentData, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        console.log('[API] Payment successful via alternative endpoint:', altResponse.data);
        return altResponse.data;
        
      } catch (altError) {
        console.log('[API] Alternative endpoint also failed:', altError.response?.status, altError.response?.data?.msg || altError.message);
        
        // Check if this is a timing issue with booking creation
        if (error.response?.data?.msg?.includes('Invalid booking reference ID')) {
          console.log('[API] Booking reference invalid - may be timing issue or booking not fully created');
          
          // Try one more time with longer delay
          console.log('[API] Attempting final retry after 3 second delay...');
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          try {
            const retryResponse = await api.post("/api/payment/process", formattedPaymentData, {
              headers: {
                'Content-Type': 'application/json'
              }
            });
            
            console.log('[API] Retry payment successful:', retryResponse.data);
            return retryResponse.data;
            
          } catch (retryError) {
            console.log('[API] Final retry also failed:', retryError.response?.data?.msg || retryError.message);
          }
        }
        
        // All payment methods failed - throw error
        throw new Error('Payment processing failed: All endpoints returned 404 or are unavailable');
      }
    } else {
      // For non-404 errors, throw the original error
      throw error;
    }
  }
};

// Get saved credit cards for a passenger
export const getSavedCreditCards = async (passengerId) => {
  const start = Date.now();
  try {
    const response = await api.get(`/quote_api/get_creditcards/${passengerId}`);
    const end = Date.now();
    console.log(`[API] /quote_api/get_creditcards/${passengerId} response time: ${end - start} ms`);
    return response.data;
  } catch (error) {
    const end = Date.now();
    console.log(`[API] /quote_api/get_creditcards/${passengerId} response time (error): ${end - start} ms`);
    console.error("Error fetching saved credit cards:", error);
    // Return empty array if no cards found instead of throwing error
    return [];
  }
};
